import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useQuery } from '@tanstack/react-query';
import { notificationsApi } from '../api/notificationsApi';
import type { GetNotificationUsersParams, NotificationUser } from '../api/notificationsApi';
import { filterOptionsApi } from '../../../../shared/components/Filters/api/filterOptions';
import dayjs from 'dayjs';

interface UserSelectionPanelProps {
  isCentralAdmin: boolean;
  clientsList: { id: string; name: string }[];
  selectedUserIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

const WEAR_JEOPARDY_OPTIONS = [
  { value: 'not_worn', label: 'Not worn' },
  { value: 'intermittent_wear', label: 'Intermittent wear' },
  { value: 'not_connected', label: 'Not connected' },
  { value: 'recently_connected', label: 'Recently connected' },
  { value: 'wearing_consistently', label: 'Wearing consistently' },
];

const CONNECTED_STATUS_OPTIONS = [
  { value: 'connected', label: 'Connected' },
  { value: 'reconnect', label: 'Reconnect needed' },
  { value: 'not_connected', label: 'Not connected' },
];

const HEALTH_PERIOD_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
];

const AT_RISK_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const CARDIOVASCULAR_OPTIONS = [
  { value: 'red', label: 'Red' },
  { value: 'amber', label: 'Amber' },
];

const ACTIVITY_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'lapsed', label: 'Lapsed' },
];

const STATUS_LABELS: Record<string, string> = {
  not_worn: 'Not worn',
  intermittent_wear: 'Intermittent',
  not_connected: 'Not connected',
  recently_connected: 'Recently connected',
  wearing_consistently: 'Wearing consistently',
  connected: 'Connected',
  reconnect: 'Reconnect needed',
};

const getWearChipColor = (status: string): 'warning' | 'success' | 'default' => {
  if (status === 'not_worn' || status === 'intermittent_wear') return 'warning';
  if (status === 'recently_connected' || status === 'wearing_consistently') return 'success';
  return 'default';
};

const getConnectedChipColor = (status: string): 'warning' | 'success' | 'default' => {
  if (status === 'connected') return 'success';
  if (status === 'reconnect') return 'warning';
  return 'default';
};

export const UserSelectionPanel: React.FC<UserSelectionPanelProps> = ({
  isCentralAdmin,
  clientsList,
  selectedUserIds,
  onSelectionChange,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [clientId, setClientId] = useState('');
  const [departments, setDepartments] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [depots, setDepots] = useState<string[]>([]);
  const [jobRoles, setJobRoles] = useState<string[]>([]);
  const [wearJeopardy, setWearJeopardy] = useState<string[]>([]);
  const [connectedStatus, setConnectedStatus] = useState<string[]>([]);
  const [healthPeriod, setHealthPeriod] = useState('');
  const [atRisk, setAtRisk] = useState<string[]>([]);
  const [cardiovascular, setCardiovascular] = useState<string[]>([]);
  const [activityStatus, setActivityStatus] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onSelectionChange([]);
  }, [
    clientId,
    departments,
    regions,
    depots,
    jobRoles,
    wearJeopardy,
    connectedStatus,
    healthPeriod,
    atRisk,
    cardiovascular,
    activityStatus,
  ]);

  const effectiveClientId = clientId || undefined;

  const { data: departmentOptions } = useQuery({
    queryKey: ['notif-filter-departments', effectiveClientId],
    queryFn: () => filterOptionsApi.getDepartments(effectiveClientId, true),
    staleTime: 10 * 60 * 1000,
  });

  const { data: regionOptions } = useQuery({
    queryKey: ['notif-filter-regions', effectiveClientId],
    queryFn: () => filterOptionsApi.getRegions(effectiveClientId),
    staleTime: 10 * 60 * 1000,
  });

  const { data: depotOptions } = useQuery({
    queryKey: ['notif-filter-depots', effectiveClientId],
    queryFn: () => filterOptionsApi.getDepots(effectiveClientId),
    staleTime: 10 * 60 * 1000,
  });

  const { data: jobRoleOptions } = useQuery({
    queryKey: ['notif-filter-jobroles', effectiveClientId],
    queryFn: () => filterOptionsApi.getJobRoles(effectiveClientId),
    staleTime: 10 * 60 * 1000,
  });

  const queryParams = useMemo((): GetNotificationUsersParams => {
    const params: GetNotificationUsersParams = {};
    if (clientId) params.client_id = clientId;
    if (departments.length) params.department = departments;
    if (regions.length) params.region = regions;
    if (depots.length) params.depot = depots;
    if (jobRoles.length) params.job_role = jobRoles;
    if (wearJeopardy.length) params.wear_jeopardy = wearJeopardy;
    if (connectedStatus.length) params.connected_status = connectedStatus;
    if (healthPeriod && (atRisk.length || cardiovascular.length || activityStatus.length)) {
      const end = dayjs().format('YYYY-MM-DD');
      const start = dayjs().subtract(Number(healthPeriod), 'day').format('YYYY-MM-DD');
      if (atRisk.length) params.at_risk = atRisk;
      if (cardiovascular.length) params.cardiovascular = cardiovascular;
      if (activityStatus.length) params.activity_status = activityStatus;
      params.health_period_start = start;
      params.health_period_end = end;
    }
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [
    clientId,
    departments,
    regions,
    depots,
    jobRoles,
    wearJeopardy,
    connectedStatus,
    healthPeriod,
    atRisk,
    cardiovascular,
    activityStatus,
    debouncedSearch,
  ]);

  const { data, isFetching } = useQuery({
    queryKey: ['notification-users', queryParams],
    queryFn: () => notificationsApi.getUsers(queryParams),
  });

  const users = data?.users ?? [];

  const handleToggle = (userId: string) => {
    const current = new Set(selectedUserIds);
    if (current.has(userId)) {
      current.delete(userId);
    } else {
      current.add(userId);
    }
    onSelectionChange([...current]);
  };

  const handleSelectAll = () => {
    const allIds = new Set([...selectedUserIds, ...users.map((u) => u.id)]);
    onSelectionChange([...allIds]);
  };

  const handleClear = () => {
    onSelectionChange([]);
  };

  const hasAnyFilter =
    departments.length > 0 ||
    regions.length > 0 ||
    depots.length > 0 ||
    jobRoles.length > 0 ||
    wearJeopardy.length > 0 ||
    connectedStatus.length > 0 ||
    healthPeriod !== '' ||
    atRisk.length > 0 ||
    cardiovascular.length > 0 ||
    activityStatus.length > 0;

  const handleClearFilters = () => {
    setDepartments([]);
    setRegions([]);
    setDepots([]);
    setJobRoles([]);
    setWearJeopardy([]);
    setConnectedStatus([]);
    setHealthPeriod('');
    setAtRisk([]);
    setCardiovascular([]);
    setActivityStatus([]);
  };

  const getUserDisplayName = (user: NotificationUser) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
    return name || user.email;
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        {isCentralAdmin && (
          <FormControl size='small' fullWidth>
            <InputLabel id='notif-users-client-label'>Client (optional)</InputLabel>
            <Select
              labelId='notif-users-client-label'
              label='Client (optional)'
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <MenuItem value=''>All clients</MenuItem>
              {clientsList.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          size='small'
          fullWidth
          placeholder='Search by name or email...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Stack direction='row' spacing={1} alignItems='center'>
          <Button size='small' startIcon={<FilterListIcon />} onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide filters' : 'Advanced filters'}
          </Button>
          {hasAnyFilter && (
            <Button size='small' onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}
        </Stack>

        <Collapse in={showFilters}>
          <Box sx={{ pl: 1, pt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  options={departmentOptions?.data ?? []}
                  getOptionLabel={(o) => o.name}
                  value={(departmentOptions?.data ?? []).filter((o) => departments.includes(o.id))}
                  onChange={(_, newVal) => setDepartments(newVal.map((v) => v.id))}
                  renderInput={(params) => <TextField {...params} label='Department' />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  options={regionOptions?.data ?? []}
                  getOptionLabel={(o) => o.name}
                  value={(regionOptions?.data ?? []).filter((o) => regions.includes(o.id))}
                  onChange={(_, newVal) => setRegions(newVal.map((v) => v.id))}
                  renderInput={(params) => <TextField {...params} label='Region' />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  options={depotOptions?.data ?? []}
                  getOptionLabel={(o) => o.name}
                  value={(depotOptions?.data ?? []).filter((o) => depots.includes(o.id))}
                  onChange={(_, newVal) => setDepots(newVal.map((v) => v.id))}
                  renderInput={(params) => <TextField {...params} label='Depot' />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  options={jobRoleOptions?.data ?? []}
                  getOptionLabel={(o) => o.name}
                  value={(jobRoleOptions?.data ?? []).filter((o) => jobRoles.includes(o.id))}
                  onChange={(_, newVal) => setJobRoles(newVal.map((v) => v.id))}
                  renderInput={(params) => <TextField {...params} label='Job Role' />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  options={WEAR_JEOPARDY_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  value={WEAR_JEOPARDY_OPTIONS.filter((o) => wearJeopardy.includes(o.value))}
                  onChange={(_, newVal) => setWearJeopardy(newVal.map((v) => v.value))}
                  renderInput={(params) => <TextField {...params} label='Device worn' />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  options={CONNECTED_STATUS_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  value={CONNECTED_STATUS_OPTIONS.filter((o) => connectedStatus.includes(o.value))}
                  onChange={(_, newVal) => setConnectedStatus(newVal.map((v) => v.value))}
                  renderInput={(params) => <TextField {...params} label='Connection' />}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant='caption' color='text.secondary' fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              Health &amp; Activity Filters
            </Typography>

            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <FormControl size='small' fullWidth>
                  <InputLabel id='notif-health-period-label'>Health filter period</InputLabel>
                  <Select
                    labelId='notif-health-period-label'
                    label='Health filter period'
                    value={healthPeriod}
                    onChange={(e) => setHealthPeriod(e.target.value)}
                  >
                    {HEALTH_PERIOD_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  disabled={!healthPeriod}
                  options={AT_RISK_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  value={AT_RISK_OPTIONS.filter((o) => atRisk.includes(o.value))}
                  onChange={(_, newVal) => setAtRisk(newVal.map((v) => v.value))}
                  renderInput={(params) => <TextField {...params} label='At-Risk Level' />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  disabled={!healthPeriod}
                  options={CARDIOVASCULAR_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  value={CARDIOVASCULAR_OPTIONS.filter((o) => cardiovascular.includes(o.value))}
                  onChange={(_, newVal) => setCardiovascular(newVal.map((v) => v.value))}
                  renderInput={(params) => <TextField {...params} label='Cardiovascular' />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Autocomplete
                  multiple
                  size='small'
                  disabled={!healthPeriod}
                  options={ACTIVITY_STATUS_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  value={ACTIVITY_STATUS_OPTIONS.filter((o) => activityStatus.includes(o.value))}
                  onChange={(_, newVal) => setActivityStatus(newVal.map((v) => v.value))}
                  renderInput={(params) => <TextField {...params} label='Activity Status' />}
                />
              </Grid>
            </Grid>
            {!healthPeriod && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
                Select a period to enable health filters
              </Typography>
            )}
          </Box>
        </Collapse>

        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='body2' color='text.secondary'>
            {users.length} of {data?.total ?? '...'} users
          </Typography>
          <Stack direction='row' spacing={1} alignItems='center'>
            {isFetching && <CircularProgress size={16} />}
            <Button
              size='small'
              onClick={handleSelectAll}
              disabled={!users.length || (!hasAnyFilter && !debouncedSearch)}
            >
              Select all {users.length} matching
            </Button>
          </Stack>
        </Stack>

        <List dense sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {users.length === 0 && !isFetching && (
            <ListItem>
              <ListItemText primary='No users found' secondary='Try adjusting your search or filters' />
            </ListItem>
          )}
          {users.map((user) => {
            const displayName = getUserDisplayName(user);
            const hasName = Boolean(user.first_name || user.last_name);
            const locationParts = [user.department_name, user.region_name].filter(Boolean);

            return (
              <ListItemButton key={user.id} onClick={() => handleToggle(user.id)} dense>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge='start'
                    checked={selectedUserIds.includes(user.id)}
                    tabIndex={-1}
                    disableRipple
                    size='small'
                  />
                </ListItemIcon>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' fontWeight={500} noWrap>
                    {displayName}
                  </Typography>
                  {hasName && (
                    <Typography variant='caption' color='text.secondary' noWrap>
                      {user.email}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                    {locationParts.length > 0 && (
                      <Typography variant='caption' color='text.secondary' sx={{ mr: 0.5 }}>
                        {locationParts.join(' · ')}
                      </Typography>
                    )}
                    {user.wear_jeopardy_status && (
                      <Chip
                        size='small'
                        label={STATUS_LABELS[user.wear_jeopardy_status] ?? user.wear_jeopardy_status}
                        color={getWearChipColor(user.wear_jeopardy_status)}
                        variant='outlined'
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    {user.connected_status && (
                      <Chip
                        size='small'
                        label={STATUS_LABELS[user.connected_status] ?? user.connected_status}
                        color={getConnectedChipColor(user.connected_status)}
                        variant='outlined'
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </Box>
              </ListItemButton>
            );
          })}
        </List>

        {selectedUserIds.length > 0 && (
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body2'>{selectedUserIds.length} selected</Typography>
            <Button size='small' onClick={handleClear}>
              Clear
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
